import { catalog } from "../i18n.js";

/** A bot's own settings (components/AgentSettings.tsx). */
export const AGENT_SETTINGS = catalog(
  {
    name: "Name",
    title: "Title",
    optional: "(Optional)",
    titlePlaceholder: "Personal Assistant",
    titleHint: "A short job title, shown in the sidebar and never sent to the model",
    description: "Description",
    descriptionGroupPlaceholder: "What this group is for; every member reads it.",
    descriptionPlaceholder: "Personal assistant for the user. Handles scheduling, reminders and errands. Acts unless it would spend money; escalates decisions to the user.",
    descriptionGroupHint: "What this group is for; every member sees it",
    descriptionHint: "Who this bot is and what it does; this is what the model sees",

    model: "Model",
    modelHint: "The model this bot thinks with; a request is charged at that model's price",
    defaultChoice: "Default",
    defaultWith: (model: string) => `Default (${model})`,
    noLongerOffered: (model: string) => `${model} (no longer offered)`,
    runsDefault: "Runs the default model.",
    noLongerOfferedNote: "No longer offered, so it runs the default model until you pick another.",
    price: (input: string, output: string) => `${input} credits per million tokens in, ${output} out.`,

    effort: "Effort",
    effortHint: "How hard the model thinks before it answers; models that cannot take a setting ignore it",
    effortMinimal: "Minimal",
    effortLow: "Low",
    effortMedium: "Medium",
    effortHigh: "High",
    effortDefaultNote: "The model thinks as long as it does by default.",
    effortHighNote: "Thinks longest: slower, and uses more tokens, so each message costs more.",
    effortMediumNote: "Thinks a while before it answers.",
    effortLowNote: "Thinks briefly: quicker and cheaper, for simple work.",

    context: "Context",
    contextTokens: (size: string) => `${size} tokens`,
    contextHint: "How much of the conversation the model sees at once; past it, the earlier part is condensed into a note",
    gaugeHint: (input: string, output: string, window: string) => `The last request: ${input} tokens in, ${output} out, against a window of ${window}`,
    nothingSent: "Nothing sent yet",
    lastRequest: (input: string, output: string, used: string, limit: string) => `Last request: ${input} in + ${output} out, ${used} of ${limit}`,

    compactNow: "Compact now",
    compacting: "Compacting…",
    compactHint: "Condense the earlier conversation into a note now; the last exchange stays as it is",
    compactBusyHint: "It is working right now; compact once it has finished",
    writingNote: "Writing the note…",
    compactBusy: "Available once it has finished working.",
    nothingToCondense: "Nothing to condense yet: there is only the last exchange.",
    condensed: (count: number) => `Condensed ${count} earlier ${count === 1 ? "entry" : "entries"} into a note.`,

    notifications: "Notifications",
    notifyBot: "Get notified when this bot finishes or needs input",
    notifyGroup: "Get notified when this group finishes or needs input",
    save: "Save",
    cancel: "Cancel",
    appliesNext: "Changes apply from the next message.",
  },
  {
    name: "Nama",
    title: "Jabatan",
    optional: "(Opsional)",
    titlePlaceholder: "Asisten Pribadi",
    titleHint: "Jabatan singkat, ditampilkan di sidebar dan tidak pernah dikirim ke model",
    description: "Deskripsi",
    descriptionGroupPlaceholder: "Untuk apa grup ini; setiap anggota membacanya.",
    descriptionPlaceholder: "Asisten pribadi untuk pengguna. Mengurus jadwal, pengingat, dan keperluan sehari-hari. Langsung bertindak kecuali kalau harus keluar uang; keputusan diserahkan ke pengguna.",
    descriptionGroupHint: "Untuk apa grup ini; setiap anggota melihatnya",
    descriptionHint: "Siapa bot ini dan apa tugasnya; inilah yang dilihat model",

    model: "Model",
    modelHint: "Model yang dipakai bot ini untuk berpikir; setiap permintaan dikenai harga model itu",
    defaultChoice: "Bawaan",
    defaultWith: (model: string) => `Bawaan (${model})`,
    noLongerOffered: (model: string) => `${model} (tidak ditawarkan lagi)`,
    runsDefault: "Memakai model bawaan.",
    noLongerOfferedNote: "Tidak ditawarkan lagi, jadi bot memakai model bawaan sampai kamu memilih yang lain.",
    price: (input: string, output: string) => `${input} kredit per sejuta token masuk, ${output} keluar.`,

    effort: "Tingkat berpikir",
    effortHint: "Seberapa keras model berpikir sebelum menjawab; model yang tidak mendukung pengaturan ini akan mengabaikannya",
    effortMinimal: "Minimal",
    effortLow: "Rendah",
    effortMedium: "Sedang",
    effortHigh: "Tinggi",
    effortDefaultNote: "Model berpikir selama biasanya.",
    effortHighNote: "Berpikir paling lama: lebih lambat dan memakai lebih banyak token, jadi tiap pesan lebih mahal.",
    effortMediumNote: "Berpikir sebentar sebelum menjawab.",
    effortLowNote: "Berpikir singkat: lebih cepat dan murah, untuk pekerjaan sederhana.",

    context: "Konteks",
    contextTokens: (size: string) => `${size} token`,
    contextHint: "Seberapa banyak percakapan yang dilihat model sekaligus; lewat dari itu, bagian awalnya diringkas jadi catatan",
    gaugeHint: (input: string, output: string, window: string) => `Permintaan terakhir: ${input} token masuk, ${output} keluar, dari jendela ${window}`,
    nothingSent: "Belum ada yang dikirim",
    lastRequest: (input: string, output: string, used: string, limit: string) => `Permintaan terakhir: ${input} masuk + ${output} keluar, ${used} dari ${limit}`,

    compactNow: "Ringkas sekarang",
    compacting: "Meringkas…",
    compactHint: "Ringkas percakapan sebelumnya jadi catatan sekarang; percakapan terakhir tetap seperti adanya",
    compactBusyHint: "Bot sedang bekerja; ringkas setelah selesai",
    writingNote: "Menulis catatan…",
    compactBusy: "Bisa dipakai setelah bot selesai bekerja.",
    nothingToCondense: "Belum ada yang bisa diringkas: baru ada percakapan terakhir.",
    condensed: (count: number) => `${count} entri sebelumnya diringkas jadi catatan.`,

    notifications: "Notifikasi",
    notifyBot: "Dapat notifikasi saat bot ini selesai atau butuh jawabanmu",
    notifyGroup: "Dapat notifikasi saat grup ini selesai atau butuh jawabanmu",
    save: "Simpan",
    cancel: "Batal",
    appliesNext: "Perubahan berlaku mulai pesan berikutnya.",
  },
);
